import { ClaimWorkflowConfigurationOption } from '../../../api';
import { mdiTune } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import { getWorkflowStatusDescription, getWorkflowStatusLabel } from '../configuration-worker-metadata';
import { OptionField } from './option-field';
import * as S from './configuration-detail.styles';

interface IConfigurationDetailProps {
    interactionDisabled: boolean;
    store: WorkerStore;
}

type ConfiguredOption = ClaimWorkflowConfigurationOption & { key: string };

function hasOptionKey(option: ClaimWorkflowConfigurationOption): option is ConfiguredOption {
    return Boolean(option.key?.trim());
}

export const ConfigurationDetail: React.FC<IConfigurationDetailProps> = observer(({ interactionDisabled, store }) => {
    const selectedStatus = store.selectedStatus;
    const statusOptions = store.selectedStatusOptions;
    const statusLabel = selectedStatus ? getWorkflowStatusLabel(selectedStatus) : '';
    const statusDescription = selectedStatus ? getWorkflowStatusDescription(selectedStatus) : '';
    const options = useMemo(
        () => (statusOptions?.options ?? []).filter(hasOptionKey),
        [statusOptions?.options],
    );
    const { configured, total } = store.selectedStatusOptionCounts;

    if (!selectedStatus) {
        return (
            <S.DetailPanel>
                <S.EmptyState>Select a workflow status to view configuration.</S.EmptyState>
            </S.DetailPanel>
        );
    }

    return (
        <S.DetailPanel>
            <S.DetailHeader>{statusLabel}</S.DetailHeader>
            {statusDescription ? <S.DetailDescription>{statusDescription}</S.DetailDescription> : null}
            <S.Divider />

            {options.length === 0 ? (
                <S.EmptyState>This workflow status has no configurable options.</S.EmptyState>
            ) : (
                <>
                    <S.DetailToolbar>
                        <S.DetailHint>
                            <Icon path={mdiTune} size={0.7} aria-hidden={true} />
                            <span>Behaviours the worker applies to claims in this status.</span>
                        </S.DetailHint>
                        <S.ConfiguredCount>
                            {configured} of {total} settings configured
                        </S.ConfiguredCount>
                    </S.DetailToolbar>
                    <S.OptionList data-readonly={store.readOnly ? 'true' : undefined}>
                        {options.map((option) => (
                            <OptionField
                                key={option.key}
                                option={option}
                                status={selectedStatus}
                                store={store}
                                currentValue={store.getConfigValue(selectedStatus, option.key)}
                                disabled={
                                    interactionDisabled || !store.canEditOption(selectedStatus, option.key)
                                }
                                onChange={(value) => store.setOptionValue(selectedStatus, option.key, value)}
                            />
                        ))}
                    </S.OptionList>
                </>
            )}
        </S.DetailPanel>
    );
});
